import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Metric, Report, ReportLayoutType } from "@unbox/api";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export interface CreateReportInput {
  name: string;
  layout: ReportLayoutType;
  metrics: Metric[];
  templateId?: string | null;
  startDate: string | null; // ISO date string
  endDate: string | null; // ISO date string
}

export interface UpdateReportInput {
  name?: string;
  layout?: ReportLayoutType;
  metrics?: Metric[];
  templateId?: string | null;
  startDate?: string | null; // ISO date string
  endDate?: string | null; // ISO date string
}

export const useReports = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["reports", scope.merchantId, scope.workspaceId],
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].reports.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].reports.$get({ param: scope.param });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useReport = (id?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["report", scope.merchantId, scope.workspaceId, id],
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");
      if (!id) throw new Error("No report ID provided");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].reports[":reportId"].$get({
            param: { ...scope.param, reportId: id },
          })
        : await client.merchant[":merchantId"].reports[":reportId"].$get({
            param: { ...scope.param, reportId: id },
          });

      if (res.status === 404) return null;
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!id,
  });
};

export const useCreateReport = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateReportInput): Promise<{ id: string }> => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].reports.$post({
            param: scope.param,
            json: input,
          })
        : await client.merchant[":merchantId"].reports.$post({
            param: scope.param,
            json: input,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports", scope.merchantId, scope.workspaceId] });
    },
  });
};

export const useUpdateReport = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateReportInput }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].reports[":reportId"].$put({
            param: { ...scope.param, reportId: id },
            json: input,
          })
        : await client.merchant[":merchantId"].reports[":reportId"].$put({
            param: { ...scope.param, reportId: id },
            json: input,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["reports", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["report", scope.merchantId, scope.workspaceId, id] });
    },
  });
};

export const useDeleteReport = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].reports[":reportId"].$delete({
            param: { ...scope.param, reportId: id },
          })
        : await client.merchant[":merchantId"].reports[":reportId"].$delete({
            param: { ...scope.param, reportId: id },
          });

      if (!res.ok) throw await handleError(res);
    },
    onSuccess: (_, id) => {
      // Optimistically update the cache immediately
      queryClient.setQueryData(["reports", scope.merchantId, scope.workspaceId], (oldData: Report[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.filter((report) => report.id !== id);
      });
      queryClient.removeQueries({ queryKey: ["report", scope.merchantId, scope.workspaceId, id] });
      // Still invalidate to ensure consistency, but cache is already updated
      queryClient.invalidateQueries({ queryKey: ["reports", scope.merchantId, scope.workspaceId] });
    },
  });
};

export const useTemplateChartData = (templateId?: string, startDate?: string, endDate?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["template-chart-data", scope.merchantId, scope.workspaceId, templateId, startDate, endDate],
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");
      if (!templateId || !startDate || !endDate) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].metrics["template-chart-data"].$get({
            param: scope.param,
            query: { templateId, startDate, endDate },
          })
        : await client.merchant[":merchantId"].metrics["template-chart-data"].$get({
            param: scope.param,
            query: { templateId, startDate, endDate },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!templateId && !!startDate && !!endDate,
  });
};
